import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Tooltip from '@material-ui/core/Tooltip';

import { NewsType } from '../../../api/types/apiGetReport';
import Button from '../../../components/Buttons/Button';
import TravelDatesDialog from '../components/TravelDatesDialog';

import FindingsFeed from './FindingsFeed';
import useWatchdog from './useWatchdog';
import {
	emailFinePrint,
	formatDateRange,
	formatDateTime,
	formatShortDate,
	monitoringEndedLine,
	noFindingsBody,
	pendingBodyPrimary,
	pendingBodySecondary,
	pendingMetaRow,
	WATCHDOG_COPY,
} from './watchdogCopy';
import WatchdogStart from './WatchdogStart';
import { StatusChip, Toast, WatchdogDisclaimer } from './WatchdogUi';

import styles from './Watchdog.module.scss';

type NewsReportType = {
	travelReportId: string;
	travelDateFrom: string | null;
	travelDateTo: string | null;
	newsItems: NewsType[];
	destination?: string;
	onDatesSubmitted: () => void;
};

type StoreShape = { dashboard?: { personal?: { email?: string } } };

export default function NewsReport(props: NewsReportType) {
	const { travelReportId, travelDateFrom, travelDateTo, newsItems, destination, onDatesSubmitted } =
		props;

	const email = useSelector((state: StoreShape) => state?.dashboard?.personal?.email || null);

	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [toast, setToast] = useState<string | null>(null);

	const watchdog = useWatchdog({
		reportId: travelReportId,
		travelDateFrom,
		travelDateTo,
		newsItems,
	});

	const markVisitedRef = useRef(watchdog.markVisited);
	markVisitedRef.current = watchdog.markVisited;
	useEffect(() => () => markVisitedRef.current(), []);

	const destinationName = destination || 'your destination';
	const travelWindow = formatDateRange(travelDateFrom, travelDateTo);
	const monitoringEnded = !!watchdog.monitoringEnd && watchdog.monitoringEnd.getTime() < Date.now();

	const handleDatesSubmitted = () => {
		setIsDialogOpen(false);
		setToast(WATCHDOG_COPY.datesSavedToast);
		onDatesSubmitted();
	};

	const renderTopCard = () => (
		<div className={styles.card}>
			<div className={styles['card-title-row']}>
				<h2 className={styles['card-title']}>{WATCHDOG_COPY.cardTitle}</h2>
				<Tooltip title={WATCHDOG_COPY.infoTooltip} arrow>
					<button
						type="button"
						className={`material-icons-outlined ${styles['info-icon']}`}
						aria-label="What the Watchdog does"
					>
						info
					</button>
				</Tooltip>
			</div>
			<p className={styles['card-subtitle']}>{WATCHDOG_COPY.cardSubtitle}</p>
			{!!travelWindow && (
				<div className={styles['dates-line']}>
					<span>{`Travel dates: ${travelWindow}`}</span>
					<span>·</span>
					<button
						type="button"
						className={styles['dates-edit']}
						onClick={() => setIsDialogOpen(true)}
					>
						Edit
					</button>
				</div>
			)}
		</div>
	);

	const renderBody = () => {
		switch (watchdog.state) {
			// State A (§2) — no travel dates set.
			case 'no-dates':
				return (
					<section className={styles.panel}>
						<h3 className={styles['panel-headline']}>{WATCHDOG_COPY.noDatesHeadline}</h3>
						<p className={styles['panel-body']}>{WATCHDOG_COPY.noDatesBody}</p>
						<div className={styles['panel-actions']}>
							<Button variant="filled" color="primary" onClick={() => setIsDialogOpen(true)}>
								{WATCHDOG_COPY.noDatesCta}
							</Button>
						</div>
					</section>
				);

			// State C (§4) — dates set, Watchdog not started.
			case 'not-started':
				return (
					<WatchdogStart
						destination={destinationName}
						email={email}
						travelDateFrom={travelDateFrom}
						travelDateTo={travelDateTo}
						onStart={watchdog.start}
					/>
				);

			// State D (§5) — Watchdog active, first sweep pending.
			case 'pending':
				return (
					<section className={styles.panel}>
						<StatusChip label={WATCHDOG_COPY.activeChip} tone="active" />
						<div className={styles.radar} />
						<h3 className={styles['panel-headline']}>{WATCHDOG_COPY.pendingHeadline}</h3>
						<p className={styles['panel-body']}>{pendingBodyPrimary(destinationName)}</p>
						<p className={styles['panel-body']}>
							{pendingBodySecondary(formatShortDate(watchdog.monitoringEnd))}
						</p>
						<div className={styles['skeleton-list']}>
							<div className={styles.skeleton} />
							<div className={styles.skeleton} />
						</div>
						<p className={styles['meta-row']}>
							{pendingMetaRow(
								formatShortDate(watchdog.monitoringStart),
								formatShortDate(watchdog.monitoringEnd),
								email,
							)}
						</p>
					</section>
				);

			// State E (§6) — sweep complete, no findings.
			case 'empty':
				return (
					<section className={styles.panel}>
						<StatusChip label={WATCHDOG_COPY.activeChip} tone="active" />
						<h3 className={styles['panel-headline']}>{WATCHDOG_COPY.noFindingsHeadline}</h3>
						<p className={styles['panel-body']}>
							{noFindingsBody(
								formatDateTime(watchdog.lastSweepAt) || 'just now',
								watchdog.nextSweepInHours,
							)}
						</p>
						<p className={styles['fine-print']}>{emailFinePrint(email)}</p>
					</section>
				);

			// State F (§7) — monitoring window ended with nothing on file.
			case 'ended':
				return (
					<section className={styles.panel}>
						<StatusChip label={WATCHDOG_COPY.endedChip} tone="ended" />
						<h3 className={styles['panel-headline']}>{WATCHDOG_COPY.noFindingsHeadline}</h3>
						<p className={styles['panel-body']}>
							{monitoringEndedLine(formatShortDate(watchdog.monitoringEnd))}
						</p>
					</section>
				);

			default:
				return (
					<FindingsFeed
						items={newsItems}
						latestBatchCutoff={watchdog.latestBatchCutoff}
						lastSweepAt={watchdog.lastSweepAt}
						nextSweepInHours={watchdog.nextSweepInHours}
						monitoringEnded={monitoringEnded}
						monitoringEndLabel={formatShortDate(watchdog.monitoringEnd)}
					/>
				);
		}
	};

	return (
		<div className={styles.watchdog}>
			{renderTopCard()}
			{renderBody()}
			{watchdog.state !== 'no-dates' && <WatchdogDisclaimer />}

			<TravelDatesDialog
				open={isDialogOpen}
				travelReportId={travelReportId}
				initialStartDate={travelDateFrom}
				initialEndDate={travelDateTo}
				onClose={() => setIsDialogOpen(false)}
				onSubmitted={handleDatesSubmitted}
			/>

			{!!toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
		</div>
	);
}
